import React, { useCallback } from "react";
import getServiceEndpoint from "@/api/ServiceEndpoint";
import {
  HubConnectionState,
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { useStreamingMessageHandler } from "./useStreamingMessageHandler";

/**
 * Interface defining the functionality provided by the useSocket hook
 */
interface UseSocket {
  socketCreated: boolean;
  /** Current state of the SignalR connection */
  connectionState: HubConnectionState;

  /** Subscribe to real-time updates for a specific client */
  subscribe: (clientKey: string) => Promise<void>;

  /** Unsubscribe from real-time updates */
  unsubscribe: () => Promise<void>;
}

/**
 * Hook that provides WebSocket connection management for real-time communication
 * Uses Microsoft SignalR to establish a connection to the notification hub
 *
 * @returns An object with methods and state for managing the WebSocket connection
 *
 * @example
 * // Example usage
 * const { connectionState, subscribe, unsubscribe } = useSocket();
 * subscribe('client-123');
 */
export const useSocket = (): UseSocket => {
  const initialised = React.useRef(false);
  const [connection, setConnection] = React.useState<HubConnection | undefined>(
    undefined
  );
  const [connectionState, setConnectionState] =
    React.useState<HubConnectionState>(HubConnectionState.Disconnected);

  const messageHandler = useStreamingMessageHandler();

  /**
   * Subscribe to real-time updates for a specific client
   *
   * @param clientKey - The unique identifier for the client to subscribe to
   */
  const subscribe = useCallback(
    async (clientKey: string) => {
      if (connection && connectionState.toString() !== "Connected") {
        await connection.start();
        setConnectionState(connection.state);
        if (connection.state === HubConnectionState.Connected) {
          connection.invoke("SubscribeToClient", clientKey);
        }
      }
    },
    [connection, connectionState]
  );

  /**
   * Unsubscribe from real-time updates
   * Stops the SignalR connection
   */
  const unsubscribe = useCallback(async () => {
    if (connection) {
      await connection.stop();
      setConnectionState(connection.state);
    }
  }, [connection]);

  /**
   * Creates a new SignalR connection to the notification hub
   * Sets up event handlers for connection state changes and message receiving
   */
  const createConnection = async () => {
    try {
      const token = localStorage.getItem("token")?.replace("bearer ", "");

      const conn: HubConnection = new HubConnectionBuilder()
        .withUrl(`${getServiceEndpoint()}/notifications`, {
          accessTokenFactory: () => token || "",
          withCredentials: false,
        })
        .configureLogging(LogLevel.Information)
        .build();
      conn.serverTimeoutInMilliseconds = 60000;
      conn.keepAliveIntervalInMilliseconds = 20000;
      conn.onclose(() => {
        setConnectionState(HubConnectionState.Disconnected);
      });
      conn.onreconnecting(() => {
        setConnectionState(HubConnectionState.Reconnecting);
      });
      conn.onreconnected(() => {
        setConnectionState(HubConnectionState.Connected);
      });
      conn.on("ReceiveMessage", (message: any) => {
        messageHandler.handleMessage(message);
      });
      setConnection(conn);
    } catch (error) {
      console.log(error);
    }
  };

  // Initialize the connection when the component mounts
  React.useEffect(() => {
    // using ref due to this change (which is not an issue in a production build)
    // https://upmostly.com/tutorials/why-is-my-useeffect-hook-running-twice-in-react
    if (!initialised.current) {
      initialised.current = true;
      createConnection();
    }

    // Clean up the connection when the component unmounts
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    connectionState,
    subscribe,
    unsubscribe,
    socketCreated: connection !== undefined,
  };
};
